import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { inr } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { BarChart3, TrendingUp, CalendarDays, Table as TableIcon } from "lucide-react";

export default function AdminReports() {
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/revenue?period=${period}`)
      .then((r) => {
        setData(r.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [period]);

  const total = data.reduce((s, d) => s + d.revenue, 0);

  return (
    <div data-testid="admin-reports" className="space-y-8">
      {/* Header & Controls */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-white/10 pb-6"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
            <BarChart3 size={16} /> ANALYTICS & INSIGHTS
          </div>
          <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            Revenue Reports
          </h1>
        </div>

        {/* Period Selector Pills */}
        <div className="flex items-center gap-2 bg-black/60 p-1.5 rounded-2xl border border-white/15">
          <button
            onClick={() => setPeriod("monthly")}
            className={`px-5 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-all flex items-center gap-2 ${
              period === "monthly"
                ? "border border-amber-400/80 bg-gradient-to-r from-amber-500/25 to-amber-500/10 text-amber-300 shadow-md shadow-yellow-950/40"
                : "text-white/60 hover:text-white hover:bg-white/5"
            }`}
          >
            <CalendarDays size={14} /> Monthly
          </button>
          <button
            onClick={() => setPeriod("daily")}
            className={`px-5 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-all flex items-center gap-2 ${
              period === "daily"
                ? "border border-amber-400/80 bg-gradient-to-r from-amber-500/25 to-amber-500/10 text-amber-300 shadow-md shadow-yellow-950/40"
                : "text-white/60 hover:text-white hover:bg-white/5"
            }`}
          >
            <TrendingUp size={14} /> Daily
          </button>
        </div>
      </motion.div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="premium-spinner mb-3"></div>
          <div className="text-xs text-emerald-400 font-bold uppercase tracking-widest animate-pulse">Loading Reports...</div>
        </div>
      ) : (
        <>
          {/* Chart Container */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="rounded-2xl border border-amber-400/80 p-6 lg:p-8 bg-gradient-to-b from-amber-950/40 via-black/95 to-hpaper shadow-xl shadow-yellow-950/20"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-white/10">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold mb-1">
                  TOTAL REVENUE ({period.toUpperCase()})
                </div>
                <div className="text-3xl lg:text-4xl font-heading font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400">
                  {inr(total)}
                </div>
              </div>

              <div className="text-xs text-white/50 font-semibold bg-black/60 px-4 py-2 rounded-xl border border-white/10">
                Data entries: <span className="text-amber-300 font-bold">{data.length}</span>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="period" stroke="#a3a3a3" tick={{ fontSize: 11, fontWeight: "bold" }} />
                <YAxis stroke="#a3a3a3" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: "#0c0c0c",
                    border: "1px solid #fbbf24",
                    borderRadius: "14px",
                    color: "#fff",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.8)",
                    fontWeight: "bold",
                  }}
                  formatter={(val) => [inr(val), "Revenue"]}
                />
                <Bar dataKey="revenue" fill="#f59e0b" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Detailed Table */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="rounded-2xl border border-hgold/70 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 p-6 lg:p-8"
          >
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold mb-6 flex items-center gap-2">
              <TableIcon size={16} /> PERIOD BREAKDOWN TABLE
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b-2 border-white/10 text-amber-400 font-extrabold uppercase text-[11px] tracking-wider">
                    <th className="pb-4 font-bold">Period</th>
                    <th className="pb-4 font-bold">Total Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {data.map((d) => (
                    <tr key={d.period} className="hover:bg-white/5 transition-colors">
                      <td className="py-4 font-bold text-white tracking-wide">{d.period}</td>
                      <td className="py-4 font-bold text-amber-300 text-base">{inr(d.revenue)}</td>
                    </tr>
                  ))}
                  {data.length === 0 && (
                    <tr>
                      <td colSpan="2" className="text-center py-12 text-white/50 text-base font-medium">
                        No completed booking revenue recorded for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}
